import { defineStore } from "pinia";
import { EmbedType, Keyable } from "@/providers/analytics.provider";
import { usePlayerStore } from "@/store/player.store";
import { Post } from "@/models/post";
import { useSaveAnalyticsEventMutation } from "@/graphql/API";
import { provideApolloClient } from "@vue/apollo-composable";
import { apolloClient } from "@/providers/graphql.provider";

export enum AnalyticsAction {
    SurveyLinkOpened = "SurveyLinkOpened",
    VideoPrerollCompleted = "VideoPrerollCompleted",
    NPSScoreSubmitted = "NPSScoreSubmitted",
    CSATScoreSubmitted = "CSATScoreSubmitted",
    VoiceAnswerSubmitted = "VoiceAnswerSubmitted",
    SecondVoiceAnswerSubmitted = "SecondVoiceAnswerSubmitted",
    TextAnswerSubmitted = "TextAnswerSubmitted",
    SecondTextAnswerSubmitted = "SecondTextAnswerSubmitted",
    NameSubmitted = "NameSubmitted",
    EmailSubmitted = "EmailSubmitted",
    TermsConditionsAndPrivacyPolicyAccepted = "TermsConditionsAndPrivacyPolicyAccepted",
    ThankYouLinkClicked = "ThankYouLinkClicked",
}

export interface AnalyticsStoreInterface {
    embedType?: EmbedType;
}

export interface EmbedClickData {
    position: string;
    post_id?: string;
    profile_id?: string;
    profile_url?: string;
}

export interface ShareClickData {
    destination: string;
    post_id?: string;
    profile_id?: string;
    profile_url?: string;
}

export const useAnalyticsStore = defineStore({
    id: "analytics",
    state(): AnalyticsStoreInterface {
        return {
            embedType: undefined,
        };
    },
    getters: {
        encodedPostURL(): string {
            return encodeURIComponent(this.postShareURL);
        },
        shareVideoUrl(): string | undefined {
            const post: Post | undefined = usePlayerStore().getActivePost;
            if (post && post.shareVideoWithTranscriptURL) {
                return post.shareVideoWithTranscriptURL;
            }
            return undefined;
        },
        postShareURL(): string {
            const post: Post | undefined = usePlayerStore().getActivePost;

            return `${post?.shareURL}?${this.campaignParam}`;
        },
        postShareID(): string {
            const post: Post | undefined = usePlayerStore().getActivePost;

            return `${post?.id}`;
        },
        portalShareURL(): string {
            return `https://beams.fm/?${this.campaignParam}`;
        },
        campaignParam(): string {
            const referrer = document.referrer ? encodeURIComponent(document.referrer.split("/")[2]) : "not_embedded";
            return `ct=emb_${referrer}`;
        },
    },
    actions: {
        setEmbedType(embedType: EmbedType) {
            this.embedType = embedType;
        },
        trackEvent(eventName: string, params: Keyable): void {
            this.$analytics.trackEvent(eventName, this.embedType, params);
        },
        trackPageView(params: Keyable): void {
            this.$analytics.trackPageView(document.location.origin + document.location.pathname, { ...params, ...{ type: this.embedType } });
        },
        trackClickOut(position: string): void {
            const post: Post | undefined = usePlayerStore().activePost;
            const embedClickData: EmbedClickData = {
                position: position,
                post_id: post?.id,
                profile_id: post?.profile.id,
                profile_url: post?.profile.url,
            };
            this.$analytics.trackEvent("embed_click_out", this.embedType, embedClickData);
        },
        trackShareClick(destination: string): void {
            const post: Post | undefined = usePlayerStore().activePost;

            const shareClickData: ShareClickData = {
                destination: destination,
                post_id: post?.id,
                profile_id: post?.profile.id,
                profile_url: post?.profile.url,
            };
            this.$analytics.trackEvent("embed_share_click", this.embedType, shareClickData);
        },
        sendAnalyticsEvent(action: AnalyticsAction, campaignID: string): void {
            provideApolloClient(apolloClient);

            const { mutate } = useSaveAnalyticsEventMutation({
                variables: {
                    campaignID: campaignID,
                    action: action,
                },
            });

            mutate().catch((error) => {
                console.error("Error saving event: ", error);
            });
        },
    },
});
